import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { FIELD_FONT } from "./kitUi";

export interface MultiSelectOption { id: string; name: string; hint?: string }

export interface MultiSelectProps {
	caption: string;
	options: MultiSelectOption[];
	value: string[];
	onChange: (ids: string[]) => void;
	width?: string;
	disabled?: boolean;
	allLabel?: string;
	searchable?: boolean;
}

/**
 * Compact multi-select (kits overhaul, Sep 2026): a button that reads "Todos", one name, or "N seleccionados";
 * clicking opens a panel with search, "Todos" and checkboxes. No <label> elements (global CSS positions labels absolutely).
 */
const MultiSelect: React.FC<MultiSelectProps> = ({ caption, options, value, onChange, width, disabled, allLabel, searchable }) => {
	const [open, setOpen] = useState<boolean>(false);
	const [query, setQuery] = useState<string>("");
	const box = useRef<HTMLDivElement>(null);
	const panel = useRef<HTMLDivElement>(null);
	/** Moves the panel left when it would go past the right edge of the screen (phones). */
	const [shift, setShift] = useState<number>(0);
	const canSearch = searchable ?? options.length > 8;

	useEffect(() => {
		if (!open) return;
		const onDown = (event: MouseEvent) => { if (box.current && !box.current.contains(event.target as Node)) setOpen(false); };
		const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
	}, [open]);

	useLayoutEffect(() => {
		if (!open) { setShift(0); return; }
		if (!panel.current) return;
		const rect = panel.current.getBoundingClientRect();
		const overflow = rect.right - shift - (window.innerWidth - 8);
		const next = overflow > 0 ? -Math.min(overflow, Math.max(0, rect.left - shift - 8)) : 0;
		if (next !== shift) setShift(next);
	}, [open, query, options.length]);

	const selectedNames = useMemo(() => options.filter((option) => value.includes(option.id)).map((option) => option.name), [options, value]);
	const summary = value.length < 1 ? (allLabel || "Todos") : value.length === 1 ? (selectedNames[0] || "1 seleccionado") : `${value.length} seleccionados`;
	const visible = query.trim() ? options.filter((option) => (option.name + " " + (option.hint || "")).toLowerCase().includes(query.trim().toLowerCase())) : options;

	const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

	return <div ref={box} style={{ display: "flex", flexDirection: "column", gap: "2px", flex: "0 0 auto", width: width || "180px", position: "relative" }}>
		<span style={{ fontSize: "11px", color: "var(--wa-apagado)", lineHeight: 1 }}>{caption}</span>
		<button type="button" disabled={disabled} onClick={() => setOpen(!open)} title={selectedNames.join(", ")}
			style={{ height: "34px", padding: "0 30px 0 10px", textAlign: "left", fontSize: "13px", background: "var(--wa-superficie)", border: "1px solid " + (value.length ? "var(--wa-acento)" : "var(--wa-filo-2)"), borderRadius: "6px", color: value.length ? "var(--wa-tinta)" : "var(--wa-apagado)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", position: "relative", fontWeight: value.length ? 600 : 400 }}>
			{summary}
			<span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "10px", color: "var(--wa-apagado)" }}>{open ? "▲" : "▼"}</span>
		</button>
		{open ? <div ref={panel} style={{ position: "absolute", top: "54px", left: shift + "px", zIndex: 1500, minWidth: "100%", width: "max-content", maxWidth: "min(380px, calc(100vw - 32px))", background: "var(--wa-superficie)", border: "1px solid var(--wa-filo-2)", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)", padding: "8px" }}>
			{canSearch ? <input type="text" className="form-control" autoFocus placeholder="Buscar…" value={query} onChange={(event) => setQuery(event.target.value)} style={{ height: "30px", fontSize: FIELD_FONT, marginBottom: "6px" }} /> : null}
			<div onClick={() => onChange([])} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 6px", cursor: "pointer", borderRadius: "6px", background: value.length < 1 ? "var(--wa-acento-suave)" : "transparent", fontSize: "13px", fontWeight: 600 }}>
				<span style={{ width: "16px", height: "16px", border: "2px solid " + (value.length < 1 ? "var(--wa-acento)" : "var(--wa-filo-3)"), borderRadius: "4px", background: value.length < 1 ? "var(--wa-acento)" : "var(--wa-superficie)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--wa-superficie)", fontSize: "11px" }}>{value.length < 1 ? "✓" : ""}</span>
				{allLabel || "Todos"}
			</div>
			<div style={{ maxHeight: "260px", overflowY: "auto" }}>
				{visible.length < 1 ? <div style={{ padding: "8px", color: "var(--wa-apagado)", fontSize: "13px" }}>Sin opciones</div> : visible.map((option) => {
					const checked = value.includes(option.id);
					return <div key={option.id} onClick={() => toggle(option.id)} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 6px", cursor: "pointer", borderRadius: "6px", background: checked ? "var(--wa-acento-suave)" : "transparent", fontSize: "13px" }}>
						<span style={{ flex: "0 0 16px", width: "16px", height: "16px", border: "2px solid " + (checked ? "var(--wa-acento)" : "var(--wa-filo-3)"), borderRadius: "4px", background: checked ? "var(--wa-acento)" : "var(--wa-superficie)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--wa-superficie)", fontSize: "11px" }}>{checked ? "✓" : ""}</span>
						<span style={{ whiteSpace: "normal", overflowWrap: "anywhere", minWidth: 0 }}>{option.name}{option.hint ? <span style={{ color: "var(--wa-apagado)", fontSize: "11px" }}> {option.hint}</span> : null}</span>
					</div>;
				})}
			</div>
			{value.length > 0 ? <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", paddingTop: "6px", borderTop: "1px solid var(--wa-filo)" }}>
				<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "12px" }} onClick={() => onChange([])}>Quitar selección</button>
				<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "12px" }} onClick={() => setOpen(false)}>Listo</button>
			</div> : null}
		</div> : null}
	</div>;
};

export default MultiSelect;
